import { useState } from 'react'
import { authedAxios, sleep } from '../utils'
import Papa from 'papaparse'

const useDataImport = () => {
  const [id, setId] = useState(null)

  const [localErrors, setLocalErrors] = useState([])
  const [serverErrors, setServerErrors] = useState([])
  const [status, setStatus] = useState('waiting')
  const [i, setI] = useState(0)
  const [numberOfChunks, setNumberOfChunks] = useState(1)

  const [continueFun, setContinueFun] = useState(null)
  const [fatalError, setFatalError] = useState(null)

  const [validationErrors, setValidationErrors] = useState(null)

  const hasValidationErrors = Object.keys(validationErrors || {}).some(
    (key) => validationErrors[key]?.length > 0
  )

  const reset = () => {
    setLocalErrors([])
    setStatus('waiting')
    setId(null)
    setI(0)
    setServerErrors([])
    setValidationErrors(null)
  }

  const abort = (inId, parser) => {
    parser?.abort()
    reset()
    return authedAxios.post(`/api/import/${inId || id}/abort`)
  }

  const sendChunk = (id, data, parser) => {
    parser.pause()

    return authedAxios
      .post(`/api/import/${id}`, {
        data,
      })
      .then(() => {
        // setI((prev) => prev + 1)
        parser.resume()
      })
      .catch((err) => {
        if (err?.response?.data?.fatal) {
          parser.abort()
          setStatus('aborted')
          setFatalError(err.response.data)
          return
        }

        parser.resume()
      })
      .finally(() => {
        setI((prev) => prev + 1)
      })
  }

  const chunk = (id) => (results, parser) => {
    const hasErrors = results.errors.length > 0

    if (hasErrors) {
      parser.pause()

      setLocalErrors((prev) => prev.concat(results.errors))

      setContinueFun(() => () => {
        sendChunk(id, results.data, parser)
        setLocalErrors([])
        setContinueFun(null)
      })
    } else {
      sendChunk(id, results.data, parser)
    }
  }

  const handleFileUpload = (file) => {
    setFatalError(null)

    const maxFileSize = 10 * 1000 * 1000
    const chunkSize = 1000 * 10

    if (file.size > maxFileSize) return // createAlert({message: 'File is too large. Max allowed is 10 MB.', severity: 'error' })

    setNumberOfChunks(Math.ceil(file.size / chunkSize))

    setStatus('uploading')

    authedAxios
      .post(`/api/import/start`)
      .then((res) => {
        setId(res.data.id)

        Papa.parse(file, {
          skipEmptyLines: true,
          header: true,
          chunkSize,
          chunk: chunk(res.data.id),
          complete: (results) => {
            if (results.meta.aborted) return abort()

            setStatus('validating')

            Promise.all([
              authedAxios.post(`/api/import/${res.data.id}/complete`),
              sleep(2000),
            ])
              .then(([res]) => {
                if (!res.data.errors && res.data.commitSuccess) {
                  return setStatus('complete')
                }

                setValidationErrors(res.data.errors)
              })
              .catch((e) => {})
          },
        })
      })
      .catch(() => {
        setStatus('failed')
      })
  }

  const commitFileUpload = () => {
    return authedAxios.post(`/api/import/${id}/commit`).then(() => {
      setStatus('complete')
    })
  }

  return {
    handleFileUpload,
    localErrors,
    progress: Math.min(i / Math.max(numberOfChunks, 1), 100),
    serverErrors,
    status,
    commitFileUpload,
    hasValidationErrors,
    fatalError,
    validationErrors,
    abort,
    continueFun,
  }
}

export default useDataImport
